import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { getShopByOwner, getShopById, getAllShops } from '../../services/shop-service';
import { addressesEqual, extractShopAddress, resolveShopName, shortAddress } from '../../lib/shop-address';

// Great-circle distance in km between two lat/long points (Haversine).
function distanceKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLon = ((bLon - aLon) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function distanceLabel(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;
}

interface ShopModalProps {
  show: boolean;
  onClose: () => void;
  allShops: any[];
  shopAccounts: any[];
  selectedShop: string | null;
  onSelectShop: (shopOwner: string, shopName: string) => void;
  onNewShopDiscovered?: (shop: any) => void;
}

type ModalView = 'list' | 'scan' | 'paste';

const CHIP_ACTIVE = 'pixel-corners-sm bg-white text-primary font-grotesk font-bold text-[12px] px-3.5 py-2';
const CHIP_INACTIVE = 'pixel-corners-sm bg-black/[.18] text-white font-grotesk font-bold text-[12px] px-3.5 py-2';

export default function ShopModal({
  show,
  onClose,
  allShops,
  shopAccounts,
  selectedShop,
  onSelectShop,
  onNewShopDiscovered,
}: ShopModalProps) {
  const [view, setView] = useState<ModalView>('list');
  const [pasteValue, setPasteValue] = useState('');
  const [error, setError] = useState('');
  const [resolving, setResolving] = useState(false);
  const [search, setSearch] = useState('');
  const [publicShops, setPublicShops] = useState<any[] | null>(null);
  const [geo, setGeo] = useState<{ lat: number; lon: number } | null>(null);
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'qr-scanner-container';

  // Load the public shop directory when the modal opens (Plan §2 Layer 3:
  // browse/search all shops, not just ones you've already visited). Falls back
  // to the caller's known-shops list if the fetch fails (offline).
  //
  // Directory rows are the SLIM projection — no owner_address (leak fix,
  // DISCOVERY_VISIBILITY_DECISION.md). For shops the customer already knows
  // (visited, or their own) we backfill `owner` from allShops by row id so the
  // Member badge and ACTIVE marker still work; for unvisited shops the owner
  // is resolved on tap via getShopById.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    getAllShops()
      .then((rows) => {
        if (cancelled) return;
        setPublicShops(
          rows.map((s) => {
            const known = allShops.find((k: any) => k.id === s.id);
            return {
              id: s.id,
              owner: known?.owner ?? null,
              name: s.name,
              description: s.description,
              imageUrl: s.image_url,
              pixelImageUrl: s.pixel_image_url,
              latitude: s.latitude ?? null,
              longitude: s.longitude ?? null,
              accentColor: s.accent_color ?? null,
              currencyName: s.currency_name ?? null,
              specials: s.specials ?? null,
            };
          })
        );
      })
      .catch(() => { if (!cancelled) setPublicShops(null); });
    return () => { cancelled = true; };
  }, [show, allShops]);

  // Opt-in location request (Plan §2 Layer 1). Deliberately NOT auto-prompted
  // on open — the barista/QR path works without it and an unsolicited location
  // prompt is hostile. Fires only when the customer taps "Nearest".
  function requestNearest() {
    if (!navigator.geolocation) { setGeoStatus('error'); return; }
    setGeoStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeo({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setGeoStatus('idle'); },
      () => setGeoStatus('error'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  // Master list = public directory (superset) or the caller's known shops.
  // Attach distance when we have both the user's location and a shop's coords,
  // filter by the search box, then sort nearest-first (located shops before
  // unlocated ones, otherwise alphabetical).
  const displayShops = useMemo(() => {
    const base = publicShops ?? allShops;
    const q = search.trim().toLowerCase();
    const withDist = base.map((s: any) => {
      const dist =
        geo && typeof s.latitude === 'number' && typeof s.longitude === 'number'
          ? distanceKm(geo.lat, geo.lon, s.latitude, s.longitude)
          : null;
      return { ...s, dist };
    });
    const filtered = q
      ? withDist.filter((s: any) => resolveShopName(s).toLowerCase().includes(q))
      : withDist;
    return filtered.sort((a: any, b: any) => {
      if (a.dist != null && b.dist != null) return a.dist - b.dist;
      if (a.dist != null) return -1;
      if (b.dist != null) return 1;
      return resolveShopName(a).localeCompare(resolveShopName(b));
    });
  }, [publicShops, allShops, search, geo]);

  const totalShopsCount = (publicShops ?? allShops).length;

  // Clean up scanner on unmount or view change
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!show) {
      setView('list');
      setPasteValue('');
      setError('');
      setResolving(false);
      setSearch('');
      setGeo(null);
      setGeoStatus('idle');
      stopScanner();
    }
  }, [show]);

  async function stopScanner() {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) { // SCANNING
          await scannerRef.current.stop();
        }
      } catch { /* ignore */ }
      scannerRef.current = null;
    }
  }

  async function startScanner() {
    setView('scan');
    setError('');

    // Give the DOM a tick to render the container
    await new Promise(r => setTimeout(r, 100));

    try {
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          // QR decoded — extract shop address
          const address = extractShopAddress(decodedText);
          if (address) {
            stopScanner();
            resolveShop(address);
          }
        },
        () => { /* ignore scan errors (no QR in frame) */ },
      );
    } catch (err: any) {
      setError('Camera access denied or unavailable');
      setView('list');
    }
  }

  async function handlePasteSubmit() {
    const address = extractShopAddress(pasteValue);
    if (!address) {
      setError('Could not find a shop address in that link');
      return;
    }
    resolveShop(address);
  }

  async function resolveShop(ownerAddress: string) {
    setError('');
    setResolving(true);

    // Check if we already have it locally (case-insensitive — DB lowercase, but be defensive)
    const local = allShops.find((s: any) => addressesEqual(s.owner, ownerAddress));
    if (local) {
      onSelectShop(local.owner, resolveShopName(local));
      setResolving(false);
      return;
    }

    // Fetch from Supabase
    try {
      const shop = await getShopByOwner(ownerAddress);
      if (shop) {
        const shopWithOwner = { ...shop, owner: shop.owner_address };
        if (onNewShopDiscovered) {
          onNewShopDiscovered(shopWithOwner);
        } else {
          onSelectShop(shop.owner_address, resolveShopName(shop));
        }
      } else {
        setError(`Shop not found for ${shortAddress(ownerAddress)} — double-check the QR or paste the full link from the barista.`);
      }
    } catch {
      setError('Cannot reach server — check your connection');
    }
    setResolving(false);
  }

  // Directory tap on a shop we don't know the owner of yet — the slim
  // projection withholds owner_address, so fetch the full public row by id
  // first, then hand off exactly like a QR/paste resolve.
  async function resolveShopFromDirectory(shop: any) {
    if (shop.owner) {
      resolveShop(shop.owner);
      return;
    }
    if (!shop.id) return;
    setError('');
    setResolving(true);
    try {
      const full = await getShopById(shop.id);
      if (full?.owner_address) {
        const shopWithOwner = { ...full, owner: full.owner_address };
        if (onNewShopDiscovered) {
          onNewShopDiscovered(shopWithOwner);
        } else {
          onSelectShop(full.owner_address, resolveShopName(full));
        }
      } else {
        setError('Shop not found — it may have just unlisted.');
      }
    } catch {
      setError('Cannot reach server — check your connection');
    }
    setResolving(false);
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-background"
        >
          {/* Full-screen surface per mockup 10c — not a floating pop-up. */}
          <div className="w-full h-full max-w-md mx-auto flex flex-col overflow-hidden bg-background">
            {/* Coral header — same visual language as CoralHeader (rounded
                bottom, decorative circle), hand-rolled here since this
                header carries the Browse/Scan/Paste chip row CoralHeader
                has no slot for. Paints under the device status bar. */}
            <div className="flex-none relative overflow-hidden bg-coral rounded-b-[24px] px-[22px] pb-[22px] pt-[calc(env(safe-area-inset-top)+16px)]">
              <div className="absolute -top-[34px] -right-[26px] w-[126px] h-[126px] pixel-circle bg-accent-alt" />
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute right-3 p-2 text-white/90 hover:text-white"
                style={{ top: 'calc(env(safe-area-inset-top) + 8px)' }}
              >
                <X size={18} />
              </button>
              <p className="relative font-heading text-[8px] text-white/85 tracking-[1px] uppercase mb-2.5">
                {totalShopsCount} shops on Bit Brew
              </p>
              <p className="relative font-grotesk font-bold text-[26px] leading-[1.1] text-white pr-8">
                Where do you get coffee?
              </p>
              <div className="relative flex gap-2 mt-[18px]">
                <button
                  onClick={() => { stopScanner(); setView('list'); setError(''); }}
                  aria-pressed={view === 'list'}
                  className={view === 'list' ? CHIP_ACTIVE : CHIP_INACTIVE}
                >
                  Browse
                </button>
                <button
                  onClick={() => {
                    if (view === 'scan') {
                      stopScanner();
                      setView('list');
                    } else {
                      startScanner();
                    }
                  }}
                  aria-pressed={view === 'scan'}
                  className={view === 'scan' ? CHIP_ACTIVE : CHIP_INACTIVE}
                >
                  Scan QR
                </button>
                <button
                  onClick={() => {
                    stopScanner();
                    setView(view === 'paste' ? 'list' : 'paste');
                    setError('');
                  }}
                  aria-pressed={view === 'paste'}
                  className={view === 'paste' ? CHIP_ACTIVE : CHIP_INACTIVE}
                >
                  Paste link
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto flex flex-col gap-3.5 px-[22px] pt-5 pb-6">
              {/* QR Scanner View */}
              {view === 'scan' && (
                <div className="bg-white pixel-corners p-[18px]">
                  <div
                    id={scannerContainerId}
                    className="w-full overflow-hidden pixel-corners border-[3px] border-primary"
                    style={{ minHeight: 280 }}
                  />
                  <p className="font-grotesk font-medium text-[13px] text-body-grey mt-3 text-center">
                    Point your camera at the shop's QR code.
                  </p>
                </div>
              )}

              {/* Paste Link View */}
              {view === 'paste' && (
                <div className="bg-white pixel-corners p-[18px] flex flex-col gap-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={pasteValue}
                      onChange={(e) => { setPasteValue(e.target.value); setError(''); }}
                      placeholder="Paste shop link or address…"
                      className="flex-1 min-w-0 pixel-corners-sm bg-background border-2 border-hairline px-[18px] py-[13px] font-grotesk font-medium text-sm text-primary outline-none focus:border-coral placeholder:text-body-grey"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') handlePasteSubmit(); }}
                    />
                    <button
                      onClick={handlePasteSubmit}
                      disabled={!pasteValue.trim() || resolving}
                      className="shrink-0 pixel-corners-sm bg-pixel-cyan text-teal-ink font-grotesk font-bold text-[13px] py-[13px] px-[18px] disabled:opacity-40"
                    >
                      {resolving ? '…' : 'Go'}
                    </button>
                  </div>
                  <p className="font-grotesk font-medium text-[12px] text-body-grey">
                    Paste the link from your barista's QR code.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <p className="font-grotesk font-medium text-[13px] text-error text-center">{error}</p>
              )}

              {/* Resolving indicator */}
              {resolving && (
                <p className="font-grotesk font-medium text-[13px] text-body-grey text-center">
                  Looking up shop…
                </p>
              )}

              {/* Divider when scan/paste is active */}
              {(view === 'scan' || view === 'paste') && allShops.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-hairline" />
                  <span className="font-grotesk font-medium text-[12px] text-body-grey">or choose below</span>
                  <div className="flex-1 h-px bg-hairline" />
                </div>
              )}

              {/* Find / Nearest controls (Plan §2: search + geolocation) */}
              {view === 'list' && (
                <div className="flex gap-2.5">
                  <div className="flex-1 min-w-0 bg-white pixel-corners-sm px-[18px] py-[13px]">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search shops…"
                      className="w-full min-w-0 bg-transparent font-grotesk font-medium text-sm text-primary outline-none placeholder:text-body-grey"
                    />
                  </div>
                  <button
                    onClick={requestNearest}
                    className="shrink-0 pixel-corners-sm bg-pixel-cyan text-teal-ink font-grotesk font-bold text-[13px] py-[13px] px-[18px]"
                  >
                    {geoStatus === 'loading' ? '…' : 'Nearest'}
                  </button>
                </div>
              )}
              {view === 'list' && geoStatus === 'error' && (
                <p className="font-grotesk font-medium text-[12px] text-error">
                  Location unavailable — showing all shops.
                </p>
              )}

              {/* Shop List */}
              {displayShops.length === 0 ? (
                <p className="font-grotesk font-medium text-[13px] text-body-grey text-center py-6">
                  {search.trim() ? 'No shops match that name.' : 'No shops available yet.'}
                </p>
              ) : (
                displayShops.map((shop: any) => {
                  const hasAccount = shop.owner
                    ? shopAccounts.find((a: any) => addressesEqual(a.shop, shop.owner))
                    : undefined;
                  const isSelected = shop.owner ? addressesEqual(selectedShop, shop.owner) : false;
                  const name = resolveShopName(shop);
                  const detailParts = [
                    hasAccount
                      ? `Member · ${hasAccount.points} ${(shop.currencyName || 'beans').toLowerCase()} · ${hasAccount.purchases} stamps`
                      : 'Not visited yet',
                    typeof shop.dist === 'number' ? distanceLabel(shop.dist) : null,
                  ].filter(Boolean);
                  const live = Array.isArray(shop.specials)
                    ? shop.specials.find((sp: any) => sp?.title && (!sp.valid_until || new Date(sp.valid_until).getTime() > Date.now()))
                    : null;
                  return (
                    <div
                      key={shop.id ?? shop.owner}
                      className="bg-white pixel-corners p-4 flex items-center gap-3.5"
                    >
                      <div className="flex-none w-[52px] h-[52px] pixel-corners border-[3px] border-primary bg-accent-alt flex items-center justify-center font-heading text-[16px] text-primary">
                        {name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-grotesk font-bold text-[17px] text-primary truncate">{name}</p>
                        <p className="font-grotesk font-medium text-[13px] text-body-grey truncate">
                          {detailParts.join(' · ')}
                        </p>
                        {live && (
                          <p className="font-grotesk font-medium text-[12px] text-coral truncate mt-0.5">
                            On special: {live.title}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => resolveShopFromDirectory(shop)}
                        className={
                          isSelected
                            ? 'flex-none pixel-corners-sm bg-accent-alt text-primary font-grotesk font-bold text-[13px] py-[11px] px-4'
                            : 'flex-none pixel-corners-sm bg-coral text-white font-grotesk font-bold text-[13px] py-[11px] px-4'
                        }
                      >
                        {isSelected ? 'Active' : 'Pick'}
                      </button>
                    </div>
                  );
                })
              )}

              {/* Fastest join route — tapping it jumps straight into the same
                  Scan QR flow as the chip above; no new route, just another
                  entry point into it (Plan §2 Layer 2). */}
              <button
                type="button"
                onClick={startScanner}
                className="bg-white pixel-corners p-[18px] text-center"
              >
                <div className="w-[88px] h-[88px] pixel-corners border-[3px] border-dashed border-hairline mx-auto mb-3.5 grid grid-cols-3 gap-1 p-3">
                  <div className="bg-hairline" /><div /><div className="bg-hairline" />
                  <div /><div className="bg-hairline" /><div />
                  <div className="bg-hairline" /><div className="bg-hairline" /><div />
                </div>
                <p className="font-grotesk font-bold text-[15px] text-primary mb-1">Or scan the counter card</p>
                <p className="font-grotesk font-medium text-[13px] text-body-grey leading-[1.45]">
                  Fastest way in — no search, no location needed.
                </p>
              </button>

              {/* Owner escape hatch. Searching for your own café and finding
                  nothing is the strongest owner-intent signal we get, so the
                  prompt gets louder on a no-match search. This is also the
                  fallback route out of cold-start if the capability fetch and
                  the sticky owner flag both miss. */}
              <button
                onClick={() => { onClose(); navigate('/shops/register'); }}
                className={`w-full pixel-corners-sm bg-white border-2 font-grotesk font-bold text-[13px] py-3 px-4 transition-colors ${
                  search.trim() && displayShops.length === 0
                    ? 'border-coral text-coral'
                    : 'border-hairline text-body-grey hover:border-coral hover:text-coral'
                }`}
              >
                Don't see your café? Register it
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
